import { fabric } from 'fabric';
import { v4 as uuidv4 } from 'uuid';

export enum VideoModeEnum {
  Editor = 'editor',
  Thumbnail = 'thumbnail',
  Preview = 'preview',
}

interface HandleVideoOptions {
  url: string;
  canvas: fabric.Canvas;
  mode: VideoModeEnum;
  fileType: string;
  left?: number;
  top?: number;
  object?: fabric.Object;
  assetId: string;
  thumbnailUrl?: string;
}

const DEFAULT_THUMBNAIL_PAUSE_TIME = 300;

const videoRenderLoops = new WeakSet<fabric.Canvas>();

const hasPlayingVideo = (canvas: fabric.Canvas) =>
  canvas.getObjects().some((obj) => {
    if (obj.data?.type !== 'video') return false;
    const element = (obj as fabric.Image).getElement();
    return (
      element instanceof HTMLVideoElement && !element.paused && !element.ended
    );
  });

// Fabric only redraws on demand, so playing videos need a per-frame render
// until every video on the canvas has stopped.
export const startVideoRenderLoop = (canvas: fabric.Canvas) => {
  if (videoRenderLoops.has(canvas)) return;
  videoRenderLoops.add(canvas);
  const render = () => {
    if (!canvas.getContext() || !hasPlayingVideo(canvas)) {
      videoRenderLoops.delete(canvas);
      return;
    }
    canvas.renderAll();
    fabric.util.requestAnimFrame(render);
  };
  fabric.util.requestAnimFrame(render);
};

const watchVideoForRender = (
  video: HTMLVideoElement,
  canvas: fabric.Canvas,
) => {
  const requestRender = () => {
    if (canvas.getContext()) canvas.requestRenderAll();
  };
  video.addEventListener('play', () => startVideoRenderLoop(canvas));
  ['loadeddata', 'seeked', 'pause', 'ended'].forEach((event) =>
    video.addEventListener(event, requestRender),
  );
};

function getVideoDimensionsOf(url: string) {
  return new Promise((resolve) => {
    const video = document.createElement('video');

    video.addEventListener(
      'loadedmetadata',
      function () {
        const height = this.videoHeight;
        const width = this.videoWidth;

        resolve({ height, width, video });
      },
      { once: true },
    );

    video.src = url;
  });
}

export const handleVideo = async ({
  url,
  mode,
  canvas,
  fileType,
  left = 0,
  top = 0,
  object,
  assetId,
  thumbnailUrl,
}: HandleVideoOptions) => {
  const { width, height, video }: any = await getVideoDimensionsOf(url);
  function getVideoElement() {
    video.id = uuidv4();
    video.width = width;
    video.height = height;
    video.muted = true;
    video.poster = thumbnailUrl || '';
    video.controls = true;
    const source = document.createElement('source');
    source.src = url;
    source.type = fileType;
    video.appendChild(source);
    return video;
  }

  const videoE = getVideoElement();
  watchVideoForRender(videoE, canvas);
  const fabVideo = new fabric.Image(videoE, {
    width,
    height,
    left,
    top,
    objectCaching: false,
  });
  fabVideo.set('data', {
    // Keep other data of the object being recreated (layer name, CSS
    // animation, ...).
    ...(object?.data || {}),
    assetId,
    type: 'video',
    url,
    fileType,
    thumbnailUrl,
    deleteVideo: () => {
      // TODO: is this the best way to handle this?
      (fabVideo.getElement() as any).pause();
      fabVideo.getElement().remove();
    },
  });
  if (object) {
    const {
      scaleX,
      scaleY,
      angle,
      selectable,
      // @ts-ignore;
      id,
      // @ts-ignore;
      auto,
      lockMovementX,
      lockMovementY,
      lockRotation,
      lockScalingX,
      lockScalingY,
      flipX,
      flipY,
      opacity,
    } = object;
    fabVideo.set({
      // @ts-ignore;
      id,
      auto,
      scaleX,
      scaleY,
      angle,
      selectable,
      lockMovementX,
      lockMovementY,
      lockRotation,
      lockScalingX,
      lockScalingY,
      flipX,
      flipY,
      opacity,
      thumbnailUrl,
    });
  }
  canvas.add(fabVideo);
  switch (mode) {
    case VideoModeEnum.Editor:
      (fabVideo.getElement() as any).play();
      break;
    case VideoModeEnum.Thumbnail:
      // TODO: will this can be a memory leak?
      (fabVideo.getElement() as any).play();
      setTimeout(() => {
        (fabVideo.getElement() as any).pause();
      }, DEFAULT_THUMBNAIL_PAUSE_TIME);
      break;
    default:
      break;
  }
  return fabVideo;
};

export const handleVideoFromFabricObject = async (
  canvas: fabric.Canvas,
  obj: fabric.Object,
  mode: VideoModeEnum,
) => {
  if (obj.data?.type === 'video' && obj.width && obj.height) {
    await handleVideo({
      canvas,
      url: obj.data.url,
      fileType: obj.data.fileType,
      mode: mode,
      left: obj.left,
      top: obj.top,
      object: obj,
      assetId: obj?.data?.assetId,
      thumbnailUrl: obj?.data?.thumbnailUrl,
    });
  }
};

export const MAX_VIDEOS_PER_SLIDE = 1;
export const VIDEO_LIMIT_MESSAGE =
  'A slide can have one video. Replace or delete the current video to use another one.';

export const getSlideVideos = (canvas: fabric.Canvas) =>
  canvas.getObjects().filter((object) => object.data?.type === 'video');

// Puts a new video where `previous` is, at the same width, and keeps its
// rotation, flip, opacity, locks, layer name and animation.
export const replaceVideo = async (
  canvas: fabric.Canvas,
  previous: fabric.Object,
  video: Pick<
    HandleVideoOptions,
    'url' | 'fileType' | 'assetId' | 'thumbnailUrl'
  >,
) => {
  const replacement = await handleVideo({
    ...video,
    canvas,
    mode: VideoModeEnum.Thumbnail,
    left: previous.left,
    top: previous.top,
    object: {
      scaleX: previous.scaleX,
      scaleY: previous.scaleY,
      angle: previous.angle,
      selectable: previous.selectable,
      lockMovementX: previous.lockMovementX,
      lockMovementY: previous.lockMovementY,
      lockRotation: previous.lockRotation,
      lockScalingX: previous.lockScalingX,
      lockScalingY: previous.lockScalingY,
      flipX: previous.flipX,
      flipY: previous.flipY,
      opacity: previous.opacity,
      // Recorded once by the caller.
      auto: true,
      data: previous.data,
    } as unknown as fabric.Object,
  });
  // The new video keeps its own aspect ratio.
  replacement.scale(previous.getScaledWidth() / (replacement.width || 1));
  replacement.setCoords();
  canvas.moveTo(replacement, canvas.getObjects().indexOf(previous));
  canvas.setActiveObject(replacement);
  // Paused rather than released: undo can bring the old video back.
  const element = (previous as fabric.Image).getElement();
  if (element instanceof HTMLVideoElement) element.pause();
  canvas.remove(previous);
  canvas.requestRenderAll();
  return replacement;
};

export const releaseVideo = (obj: fabric.Object) => {
  if (obj.data?.type !== 'video') return;
  const element = (obj as fabric.Image).getElement();
  if (!(element instanceof HTMLVideoElement)) return;
  element.pause();
  element.removeAttribute('src');
  element.querySelectorAll('source').forEach((source) => source.remove());
  element.load();
};

export const releaseVideos = (canvas: fabric.Canvas) => {
  canvas.getObjects().forEach(releaseVideo);
};
